/**
 * FilmView view component.
 * @module components/Views/FilmView
 */

import React from 'react';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { Image } from '@plone/volto/components';
import { flattenToAppURL, flattenHTMLToAppURL } from '@plone/volto/helpers';
import { TextOrBlocks } from './Commons/TextOrBlocks';
import { getFilmDatesRecurrence } from '@package/helpers';

/**
 * FilmView view component class.
 * @function FilmView
 * @params {object} content Content object.
 * @returns {string} Markup of the component.
 */

export const FilmView = ({ content }) => {
  const intl = useIntl();
  moment.locale(intl.locale);

  let video_link = null;
  if (content.link_yt && !content.link_yt.includes('embed')) {
    let video_id = content.link_yt.split('?v=')[1];
    video_link = `https://www.youtube.com/embed/${video_id}`;
  } else {
    video_link = content.link_yt;
  }

  return (
    <div id="page-document" className="ui container scheda-film">
      <h1 className="documentFirstHeading">{content.title}</h1>
      {content.description ? (
        <div className="description">{content.description}</div>
      ) : null}
      <div className="mb-5 films-template">
        <div className="film-infos-container mb-2">
          {content.image && (
            <Image
              className="mr-4"
              src={flattenToAppURL(content.image.scales.preview.download)}
              alt={content.title}
            />
          )}

          <div className="infos">
            {content.start && (
              <div className="flex">
                <div className="mr-1 font-weight-600">Data:</div>
                <div className="filmDates">
                  {getFilmDatesRecurrence(content, false)}
                </div>
              </div>
            )}
            {content.start && (
              <div className="flex">
                <div className="mr-1 font-weight-600">Orario:</div>
                <div>{moment(content.start).format('HH:mm')}</div>
              </div>
            )}

            {content.rassegna && content.rassegna.length > 0 && (
              <div className="flex">
                <div className="mr-1 font-weight-600">Rassegna:</div>
                <div>{content.rassegna.join('; ')}</div>
              </div>
            )}
            {content.regia &&
              content.regia.data.replace(/(<([^>]+)>)/g, '') !== '' && (
                <div className="flex">
                  <div className="mr-1 font-weight-600">Regia:</div>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: flattenHTMLToAppURL(content.regia?.data),
                    }}
                  />
                </div>
              )}
            {content.anno && (
              <div className="flex">
                <div className="mr-1 font-weight-600">Anno:</div>
                <div>{content.anno}</div>
              </div>
            )}
            {content.origine &&
              content.origine.data.replace(/(<([^>]+)>)/g, '') !== '' && (
                <div className="flex">
                  <div className="mr-1 font-weight-600">Origine:</div>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: flattenHTMLToAppURL(content.origine?.data),
                    }}
                  />
                </div>
              )}
            {content.durata && (
              <div className="flex">
                <div className="mr-1 font-weight-600">Durata:</div>
                <div>{content.durata}</div>
              </div>
            )}
            {content.fasce_pubblico.length > 0 && (
              <div className="flex">
                <div className="mr-1 font-weight-600">Consigli per la visione:</div>
                <div className="mr-2">
                  {content.fasce_pubblico.map((e) => e.title).join(', ')}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="other-info">
        {content.trama &&
          content.trama.data.replace(/(<([^>]+)>)/g, '') !== '' && (
            <div>
              <div
                dangerouslySetInnerHTML={{
                  __html: flattenHTMLToAppURL(content.trama?.data),
                }}
              />
            </div>
          )}
        {content?.link_biglietti && (
          <div className="link-acquisto">
            <a
              rel="noreferrer"
              target="_blank"
              href={flattenToAppURL(content.link_biglietti)}
              title="Acquista"
            >
              Acquista
            </a>
          </div>
        )}
        {video_link && (
          <div className="yt_video">
            <iframe
              src={video_link}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        )}
        {content.cast_e_credits &&
          content.cast_e_credits.data.replace(/(<([^>]+)>)/g, '') !== '' && (
            <div>
              <div className="mr-1 font-weight-600">Cast e Credits</div>
              <div
                dangerouslySetInnerHTML={{
                  __html: flattenHTMLToAppURL(content.cast_e_credits?.data),
                }}
              />
            </div>
          )}
        {content.costo &&
          content.costo.data.replace(/(<([^>]+)>)/g, '') !== '' && (
            <div>
              <div className="mr-1 font-weight-600">Costo</div>
              <div
                dangerouslySetInnerHTML={{
                  __html: flattenHTMLToAppURL(content.costo?.data),
                }}
              />
            </div>
          )}

        {content.informazioni_aggiuntive &&
          content.informazioni_aggiuntive.data.replace(/(<([^>]+)>)/g, '') !==
            '' && (
            <div>
              <div className="mr-1 font-weight-600">
                Informazioni aggiuntive
              </div>
              <div
                dangerouslySetInnerHTML={{
                  __html: flattenHTMLToAppURL(
                    content.informazioni_aggiuntive?.data,
                  ),
                }}
              />
            </div>
          )}
        {content.critica &&
          content.critica.data.replace(/(<([^>]+)>)/g, '') !== '' && (
            <div>
              <div className="mr-1 font-weight-600">Critica</div>
              <div
                dangerouslySetInnerHTML={{
                  __html: flattenHTMLToAppURL(content.critica?.data),
                }}
              />
            </div>
          )}
        {content?.link_scheda && (
          <div>
            <div className="mr-1 font-weight-600">Link</div>
            <a
              rel="noreferrer"
              target="_blank"
              href={content.link_scheda}
              title="Scheda film"
            >
              Scheda film
            </a>
          </div>
        )}
      </div>
    </div>
  );
};
